import { useTranslation } from "react-i18next";
import type { TurnUsage, VersionsUsed } from "../../../api/threads/liveMessages";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import BoundVersions from "../../versions/labels/BoundVersions";
import { useThreadContext } from "../page/threadPageContext";
import { processTerm } from "../../../api/content/processTerms";
import { classText } from "../../workflows/inputCards/typeWords";

/**
 * The versions a turn used (threads.md T-4 `versions_used`; FR-THREAD-5, NFR-16): Workflow,
 * step, prompt, data bindings and how each placeholder was filled, by their labels (ADR-0033
 * D1). No references, digests or server version (F67): the log and its export keep them.
 */
export default function VersionsUsedList({ versions }: { versions: VersionsUsed }) {
  const { t } = useTranslation();
  const { overview, workflowName } = useThreadContext();
  const rows: [string, string | null][] = [["historyPreamble", versions.history_preamble]];
  return (
    <div className="space-y-1 text-xs">
      <BoundVersions
        projectId={overview.projectId}
        // The Thread's own Workflow by name; another (a fork's origin) by what it is.
        workflow={versions.workflow === null ? null : { reference: versions.workflow, name: versions.workflow === overview.workflow ? workflowName : null }}
        contentVersion={versions.content_version}
        corpusVersion={versions.corpus_version}
        step={versions.step}
        prompt={versions.prompt}
      />
      <dl className="space-y-1">
        {rows
          .filter((row): row is [string, string] => row[1] !== null)
          .map(([name, value]) => (
            <DefinitionListItem key={name} term={name === "contentVersion" ? classText(processTerm("ContentVersion"), t) : t(`threads.versions.${name}`)} valueClassName="break-all font-mono">
              {value}
            </DefinitionListItem>
          ))}
        {versions.input_entries.length === 0 ? null : (
          <DefinitionListItem term={t("threads.versions.inputEntries")} valueClassName="font-mono">
            {versions.input_entries.map((number) => `#${String(number)}`).join(", ")}
          </DefinitionListItem>
        )}
        {versions.placeholders.map((placeholder) => (
          <DefinitionListItem key={placeholder.placeholder} term={`{${placeholder.placeholder}}`} valueClassName="break-all font-mono">
            {placeholder.selector_kind === null
              ? placeholder.source
              : `${placeholder.selector_kind}@${String(placeholder.selector_version ?? "")}`}
            {placeholder.reduction_level > 0 ? ` (${t("threads.versions.reduced", { level: placeholder.reduction_level })})` : null}
          </DefinitionListItem>
        ))}
        {versions.compaction === null ? null : (
          <DefinitionListItem term={t("threads.versions.compaction")} valueClassName="font-mono">
            {`${versions.compaction.method}@${String(versions.compaction.version)}: ${String(versions.compaction.estimated_tokens_before)} → ${String(versions.compaction.estimated_tokens_after)}`}
          </DefinitionListItem>
        )}
      </dl>
    </div>
  );
}

/** Tokens and cost of a turn (T-12). Unknown values are said to be unknown, never 0. */
export function UsageLine({ usage }: { usage: TurnUsage }) {
  const { t } = useTranslation();
  const { total } = usage;
  const { count, cost } = useUsageFormat(usage);
  return (
    <span className="app-text-muted text-[11px]">
      {t("threads.usage.summary", {
        input: count(total.input_tokens),
        output: count(total.output_tokens),
        cached: count(total.cached_input_tokens),
        cost: cost(4),
      })}
    </span>
  );
}

/** What a turn cost, the one usage figure a reader acts on; the tokens are in its tooltip and Details. */
export function CostLine({ usage }: { usage: TurnUsage }) {
  const { t } = useTranslation();
  const { total } = usage;
  const { count, cost } = useUsageFormat(usage);
  // Cents, unless the turn cost less than one: then enough digits that it doesn't read as free.
  const digits = total.cost !== null && total.cost.amount > 0 && total.cost.amount < 0.01 ? 4 : 2;
  return (
    <span className="app-text-muted text-[11px]" title={t("threads.usage.tokens", { input: count(total.input_tokens), output: count(total.output_tokens), cached: count(total.cached_input_tokens) })}>
      {t("threads.usage.cost", { cost: cost(digits) })}
    </span>
  );
}

/** Token counts and the cost in the user's language; unknown values are said to be unknown. */
function useUsageFormat(usage: TurnUsage) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage;
  return {
    count: (value: number | null) => (value === null ? t("threads.usage.unknown") : new Intl.NumberFormat(language).format(value)),
    cost: (fractionDigits: number) =>
      usage.total.cost === null
        ? t("threads.usage.unknown")
        : new Intl.NumberFormat(language, { style: "currency", currency: usage.total.cost.currency, maximumFractionDigits: fractionDigits }).format(usage.total.cost.amount),
  };
}
